import { Container, Sprite, NineSlicePlane } from 'pixi.js';
import { Tween, LoadContext } from '../../utils';
import { Grid, SimplePoint } from './Grid';
import { GAMEPLAY, LevelData } from '../../constants';
import { Focusable } from '../../ui';

// width for focus frame
const FRAME_WIDTH = 6;

export class Player extends Container implements Focusable
{
    public isMoving: boolean = false;
    private grid: Grid;
    public coord: SimplePoint = {x: 0, y:0};
    private jackie: Sprite = null;
    private focusBox: NineSlicePlane = null;

    public static preload(levelData: LevelData)
    {
        const loader = LoadContext.current;
        loader.add('player_jackie', `./assets/game/player_${levelData.CHARACTER}.png`);
        loader.add('SharedUI', './assets/shared/SharedUI{size}.json');
    }

    constructor(grid: Grid)
    {
        super();
        this.grid = grid;
        this.scale.set(grid.squareSize / 188);

        const loader = LoadContext.current;

        this.focusBox = new NineSlicePlane(loader.getSpritesheet('SharedUI').textures['highlight_frame'], FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH, FRAME_WIDTH);
        const width = this.grid.squareSize / this.scale.x;
        const height = this.grid.squareSize / this.scale.y;
        // make box just a tad bigger than whatever the size is
        this.focusBox.width = width - 6;
        this.focusBox.height = height - 6;
        this.focusBox.position.set(-this.focusBox.width / 2, -this.focusBox.height / 2);
        this.addChild(this.focusBox);
        this.focusBox.visible = false;

        this.jackie = new Sprite(loader.getTexture('player_jackie'));
        // center jackie's circle - y is ((<top to top of circle> + <circle radius>) / <image height>)
        this.jackie.anchor.set(0.5, 0.5616883116);
        this.addChild(this.jackie);
    }

    public get focus() { return this.focusBox.visible; }
    public set focus(focused) {
        this.focusBox.visible = focused;
        if (focused)
        {
            this.emit('pointerover');
            this.emit('focused');
        }
        else
        {
            this.emit('pointerout');
            this.emit('unfocused');
        }
    }

    public get focusVisible() { return this.focusBox.visible; }
    public set focusVisible(visible) {
        this.focusBox.visible = visible;
    }

    public activate()
    {
        // no op here
    }

    public setCoord({x, y}: SimplePoint)
    {
        this.coord.x = x;
        this.coord.y = y;
        this.grid.coordToPos(this.coord, this.position);
    }

    public async moveToCoord(x: number, y: number, relative = false)
    {
        // bail if moving
        if (this.isMoving)
        {
            return;
        }
        if (relative)
        {
            x += this.coord.x;
            y += this.coord.y;
        }
        // bail if trying to move outside the grid
        if (x < 0 || y < 0 || x >= this.grid.gridWidth || y >= this.grid.gridHeight)
        {
            return;
        }

        this.isMoving = true;

        const targets: {dist:number, pos:SimplePoint, dir:'N'|'S'|'E'|'W'}[] = [];
        // go to x pos first
        if (x !== this.coord.x)
        {
            targets.push({
                pos: this.grid.coordToPos({x, y: this.coord.y}),
                dist: Math.abs(x - this.coord.x),
                dir: x < this.coord.x ? 'W' : 'E'
            });
        }
        // then go to y pos
        if (y !== this.coord.y)
        {
            targets.push({
                pos: this.grid.coordToPos({x, y}),
                dist: Math.abs(y - this.coord.y),
                dir: y < this.coord.y ? 'S' : 'N'
            });
        }
        const tween = Tween.get(this.position);
        for (const target of targets)
        {
            tween.to(target.pos, GAMEPLAY.MOVE_TIME_PER_SQUARE * target.dist, 'quadInOut');
        }

        await tween.promise;

        this.isMoving = false;
        this.coord.x = x;
        this.coord.y = y;
    }

    public destroy()
    {
        super.destroy({children: true});
    }
}